import { useState } from 'react'
import { useCV } from '../hooks/useCV'
import Section from './Section'

// Columns of the matrix. Ids match the "at" field of each skill in cv-data.json.
const companies = [
  { id: 'presight', label: 'Presight', period: '2022 - now' },
  { id: 'sieuviet', label: 'Sieu Viet', period: '2020 - 2022' },
  { id: 'nth', label: 'NTH', period: '2019 - 2020' },
  { id: 'fpt', label: 'FPT', period: '2019' },
]

// Rows shown per category before "Show more"
const VISIBLE = 6

const columns = 'grid-cols-[minmax(0,1fr)_repeat(4,3.5rem)] sm:grid-cols-[minmax(0,1fr)_repeat(4,5.5rem)]'

const Skills = () => {
  const { skillCategories } = useCV()
  const [company, setCompany] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})

  const matrixCategories = skillCategories.filter((c) => c.skills.some((s) => s.at?.length))

  return (
    <Section
      id="skills"
      title="Skills"
      note="Everything I work with, then the jobs where I used each skill."
    >
      {/* Every skill in plain text so a quick scan (or Ctrl+F) finds all of them */}
      <dl>
        {skillCategories.map((category) => (
          <div key={category.id} className="grid sm:grid-cols-[10rem_1fr] gap-x-6 gap-y-1 py-4 border-b border-rule first:pt-0">
            <dt className="font-serif text-lg font-medium">{category.name}</dt>
            <dd className="text-base leading-relaxed text-mute">
              {category.skills.map((skill) => skill.name).join(', ')}
            </dd>
          </div>
        ))}
      </dl>

      <h3 className="mt-16 font-serif text-2xl font-medium">Where I used them</h3>
      <p className="mt-1 text-sm text-mute">Select a company to see only what I used there.</p>

      {/* Company filter doubles as the column header */}
      <div className={`sticky top-[5.05rem] sm:top-16 z-10 grid ${columns} items-end bg-paper/95 backdrop-blur py-2 border-b border-rule`}>
        <span />
        {companies.map((c) => (
          <button
            key={c.id}
            type="button"
            aria-pressed={company === c.id}
            onClick={() => setCompany(company === c.id ? null : c.id)}
            className={`px-1 py-1 text-center transition-colors ${
              company === c.id ? 'bg-ink text-paper' : 'text-ink hover:bg-rule/50'
            }`}
          >
            <span className="block text-[0.6875rem] sm:text-sm font-medium whitespace-nowrap">{c.label}</span>
            <span className={`hidden sm:block text-xs ${company === c.id ? 'text-paper/70' : 'text-mute'}`}>
              {c.period}
            </span>
          </button>
        ))}
      </div>

      {matrixCategories.map((category) => {
        // Skills used at the selected company first, then by how many jobs used them
        const rows = category.skills
          .filter((s) => s.at?.length)
          .map((skill, index) => ({ skill, index }))
          .sort((a, b) => {
            const aMatch = company && a.skill.at?.includes(company) ? 0 : 1
            const bMatch = company && b.skill.at?.includes(company) ? 0 : 1
            return aMatch - bMatch || (b.skill.at?.length ?? 0) - (a.skill.at?.length ?? 0) || a.index - b.index
          })
          .map((r) => r.skill)
        const isOpen = expanded[category.id]
        const shown = isOpen ? rows : rows.slice(0, VISIBLE)
        const hiddenCount = rows.length - shown.length

        return (
        <div key={category.id} className="mt-10 first-of-type:mt-6">
          <h3 className="font-serif text-xl font-medium mb-1">{category.name}</h3>
          <ul>
            {shown.map((skill) => {
              const used = skill.at ?? []
              const dimmed = company !== null && !used.includes(company)
              return (
                <li
                  key={skill.name}
                  className={`grid ${columns} items-center border-b border-rule/70 py-2 transition-[opacity,background-color] duration-200 hover:bg-ink/5 ${
                    dimmed ? 'opacity-25' : 'opacity-100'
                  }`}
                >
                  <span className="text-base pr-2">{skill.name}</span>
                  {companies.map((c) => (
                    <span key={c.id} className="flex justify-center">
                      {used.includes(c.id) ? (
                        <span role="img" aria-label={`Used at ${c.label}`} className="h-2 w-2 rounded-full bg-accent" />
                      ) : (
                        <span aria-hidden className="h-px w-2.5 bg-rule" />
                      )}
                    </span>
                  ))}
                </li>
              )
            })}
          </ul>
          {rows.length > VISIBLE && (
            <button
              type="button"
              aria-expanded={!!isOpen}
              onClick={() => setExpanded({ ...expanded, [category.id]: !isOpen })}
              className="mt-3 text-sm font-medium text-accent underline underline-offset-4 hover:no-underline"
            >
              {isOpen ? 'Show less' : `Show ${hiddenCount} more`}
            </button>
          )}
        </div>
        )
      })}

    </Section>
  )
}

export default Skills
